import { useEffect, useState } from "react";
import {
  FaArrowLeft, FaEdit, FaTrash,
  FaMapMarkerAlt, FaUsers, FaPlus,
  FaTimes, FaChevronRight, FaPhone,
  FaUserPlus, FaEnvelope
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import FullPageSpinner from "../../components/FullPageSpinner";
import { sendRequest } from "../../utils/api";
import Site from "../../models/Site";
import { useModal } from "../../context/ConfirmModalContext";
import { useToast } from "../../context/ToastContext";
import { initAllStores } from "../../utils/cache";
import User from "../../models/User";


export default function SiteListPage() {
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();

  const [loading, setLoading] = useState<boolean>(false);
  const [sites, setSites] = useState<Site[]>([]);

  const [showAssignedStaff, setShowAssignedStaff] = useState(false);
  const [assignedStaff, setAssignedStaff] = useState<(User & { siteName: string })[]>([]);

  useEffect(() => {
    const siteList = async () => {
      setLoading(true);
      try {
        const response = await sendRequest("GET", "/sites");
        setSites(response);
      } catch (err: any) {

      } finally {
        setLoading(false);
      }
    }

    siteList();
  }, []);

  const handleViewAssignedStaff = async (site: Site) => {

    setLoading(true);
    try {
      const response = await sendRequest("GET", `/sites/${site.id}/users`);
      setAssignedStaff(response);
    } catch (err: any) {

    } finally {
      setLoading(false);
    }
    setShowAssignedStaff(true);
  };



  const deleteSite = (siteId: string) => {
    return openModal(
      "Confirm Site Deletion",
      "You are about to delete this site. Do you want to continue?",
      async () => {
        setLoading(true);
        try {
          const cache = await initAllStores();

          const user = await cache.getBy("data", { id: "user", });

          if (user.role === "staff")
            return alert(
              "You don't have permission to perform this action. " +
              "Please contact your administrator."
            );

          await sendRequest("DELETE", `/sites/${siteId}/delete`);

          setSites(prev => prev.filter(site => site.id !== siteId));

          return showToast(
            "Business Site Deleted Successfully.",
            { type: "success" }
          );

        } catch (err: any) {
          showToast(
            "An error occurred while deleting the business site. Please try again.",
            { type: "error" }
          );

        } finally {
          setLoading(false);
        }
      }, true, `Yes, Delete`, "Cancel"
    );
  };


  const updateSite = async (site: Site) => {
    localStorage.setItem(
      "site",
      JSON.stringify({
        lon: site.lon,
        lat: site.lat,
        name: site.name,
        radius: site.radius,
        address: site.address
      }));

    navigate(`/settings/sites/create/${site.id}/edit`);
  }

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">

          <FullPageSpinner show={loading} />
          {showAssignedStaff && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6"
              onClick={() => setShowAssignedStaff(false)}
            >
              <div
                className="flex w-full max-w-lg max-h-[85vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="shrink-0 flex items-center justify-between border-b border-gray-100 px-6 py-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Assigned Staff
                    </h2>

                    {assignedStaff?.length > 0 && (
                      <p className="mt-1 text-sm text-gray-500 capitalize">
                        {assignedStaff[0].siteName}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => setShowAssignedStaff(false)}
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  >
                    {FaTimes({ size: 16 })}
                  </button>
                </div>

                {/* Content */}
                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                  {assignedStaff?.length > 0 ? (
                    <div className="space-y-2">
                      {assignedStaff.map((user) => (
                        <div
                          key={user.id}
                          className="flex items-center rounded-xl border border-gray-100 px-4 py-3 transition hover:bg-gray-50"
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 font-semibold text-indigo-600">
                              {`${user.firstName} ${user.lastName}`
                                .split(" ")
                                .map((name) => name[0])
                                .join("")
                                .slice(0, 2)}
                            </div>

                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-gray-900">
                                {`${user.firstName} ${user.lastName}`}
                              </p>

                              <a
                                href={`mailto:${user.email}`}
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 truncate text-xs text-gray-500 transition hover:text-indigo-600 hover:underline"
                              >
                                {FaEnvelope({ size: 10 })}
                                <span className="truncate">{user.email}</span>
                              </a>

                              <a
                                href={`tel:${user.phone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 truncate text-xs text-gray-500 transition hover:text-indigo-600 hover:underline"
                              >
                                {FaPhone({ size: 10 })}
                                <span className="truncate">{user.phone}</span>
                              </a>

                              <p className="mt-0.5 text-xs text-gray-400 capitalize">
                                {user.portfolio}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Empty State */
                    <div className="flex min-h-[300px] flex-col items-center justify-center text-center">
                      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 text-gray-400">
                        {FaUsers({ size: 26 })}
                      </div>

                      <h3 className="text-sm font-semibold text-gray-900">
                        No Staff Assigned
                      </h3>

                      <p className="mt-1 max-w-xs text-sm text-gray-500">
                        No staff members are currently assigned to this site.
                      </p>

                      <button
                        onClick={() => {
                          setShowAssignedStaff(false);
                          navigate("/staffs");
                        }}
                        className="mt-5 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700"
                      >
                        {FaUserPlus({ size: 14 })}
                        Assign Staff
                      </button>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="shrink-0 border-t border-gray-100 px-6 py-4">
                  <button
                    onClick={() => setShowAssignedStaff(false)}
                    className="w-full rounded-xl bg-gray-100 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/settings/")}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition"
            >
              {FaArrowLeft({})}
            </button>

            <div>
              <h1 className="text-2xl font-bold">Sites</h1>
              <p className="text-gray-500">
                Manage attendance locations.
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate("/settings/sites/create")}
            className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white w-10 h-10 sm:w-auto sm:h-auto sm:px-5 sm:py-3 rounded-xl transition font-semibold"
          >
            {FaPlus({ className: "text-lg" })}
            <span className="hidden sm:inline">Create Site</span>
          </button>

        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <div className="grid gap-6">
          {sites.length === 0 ? (
            <div className="bg-white border rounded-3xl shadow-sm py-20 px-8 flex flex-col items-center text-center">
              <div className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-3xl mb-6">
                {FaMapMarkerAlt({})}
              </div>

              <h2 className="text-2xl font-bold text-gray-900">
                No Sites Yet
              </h2>

              <p className="mt-3 max-w-md text-gray-500">
                You haven't created any business sites yet. Create your first attendance
                site to assign staff and enable location-based clock-in and clock-out.
              </p>

              <button
                onClick={() => navigate("/settings/sites/create")}
                className="mt-8 flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-semibold transition"
              >
                {FaPlus({})}
                Create First Site
              </button>
            </div>
          ) : (
            sites.map((site) => (
              <div
                key={site.id}
                className="bg-white rounded-2xl border shadow-sm p-6"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="text-xl font-bold capitalize">{site.name}</h2>
                    </div>

                    <div className="flex items-center gap-2 mt-3 text-gray-600 capitalize">
                      {FaMapMarkerAlt({})}
                      {site.address}
                    </div>

                    <div className="grid md:grid-cols-3 gap-5 mt-5">
                      <div>
                        <p className="text-sm text-gray-500">Latitude</p>
                        <p className="font-semibold">{site.lat}</p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-500">Longitude</p>
                        <p className="font-semibold">{site.lon}</p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-500">Radius</p>
                        <p className="font-semibold">{site.radius} metres</p>
                      </div>
                    </div>
                    <div
                      onClick={() => handleViewAssignedStaff(site)}
                      className="group mt-5 inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-indigo-600 transition-colors hover:text-indigo-800"
                    >
                      {FaUsers({ size: 15 })}

                      <span className="group-hover:underline">
                        {site?._count.assignSite} Staff Assigned
                      </span>

                      {FaChevronRight({
                        size: 12,
                        className: "transition-transform group-hover:translate-x-0.5"
                      })}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      className="flex items-center gap-2 px-5 py-3 rounded-xl border hover:bg-gray-100 transition"
                      onClick={() => updateSite(site)}
                    >
                      {FaEdit({})}
                      Edit
                    </button>

                    <button
                      className="flex items-center gap-2 px-5 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white transition"
                      onClick={() => deleteSite(site.id)}
                    >
                      {FaTrash({})}
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
